import { useCallback, useState } from "react";
import { Check, Share2 } from "lucide-react";
import type { ClipRecordingResult } from "../hooks/use-clip-recorder";

interface ShareButtonProps {
  reps: number;
  celebrityName?: string | null;
  clip?: ClipRecordingResult | null;
  className?: string;
}

/**
 * Share control for the recorded-clip overlay. Uses the native share sheet
 * (including the recorded clip as a file when supported); falls back to copying
 * the session summary to the clipboard. `className` supplies the surface
 * styling so it can sit in a row with the other clip actions.
 */
export function ShareButton({ reps, celebrityName, clip, className }: ShareButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async () => {
    const summary = celebrityName
      ? `I just did ${reps} pushups against ${celebrityName} on Hard Pushup.`
      : `I just did ${reps} pushups on Hard Pushup.`;

    const data: ShareData = { title: "Hard Pushup", text: summary };
    if (clip) {
      const file = new File([clip.blob], "pushup-clip.webm", { type: clip.mimeType });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        data.files = [file];
      }
    }

    if (navigator.share) {
      try {
        await navigator.share(data);
      } catch {
        // User dismissed the native share sheet.
      }
      return;
    }

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(summary).catch(() => undefined);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  }, [reps, celebrityName, clip]);

  return (
    <button
      type="button"
      onClick={() => void handleShare()}
      className={`inline-flex h-12 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:pointer-events-none disabled:opacity-40 ${className ?? "border border-white/15 bg-white/5 text-white hover:bg-white/10"}`}
    >
      {copied ? <Check className="size-4 text-emerald-400" /> : <Share2 className="size-4" />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}
